import React from "react";
import { ShoppingCart, Eye, ExternalLink, Clock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { purchaseDetails } from "@/lib/purchases";

const BADGE_STYLES = {
  Nouveau: "bg-emerald-500 text-white",
  Bestseller: "bg-gold text-brown-black",
  Promotion: "bg-red-500 text-white",
};

export const ShopProductCard = ({ product }) => {
  const { addItem, openCart } = useCart();
  const navigate = useNavigate();
  const img = (product.images && product.images[0]) || "";
  const hasVariants = product.variants && product.variants.length > 0;
  const displayPrice = hasVariants ? Math.min(...product.variants.map((v) => Number(v.price))) : Number(product.price);
  const purchase = purchaseDetails(product);
  const isExternal = purchase.external;
  const purchasable = isExternal || (purchase.onsite && (Boolean(product.digital_file) || (hasVariants && product.variants.some((v) => v.file))));

  const handleAdd = (e) => {
    e.preventDefault();
    if (!purchasable) return;
    if (hasVariants) {
      navigate(`/boutique/${product.id}`);
      return;
    }
    addItem(product, { qty: 1 });
    toast.success("Ajouté au panier", { description: product.name });
    openCart();
  };

  const buttonLabel = isExternal
    ? purchase.label
    : !purchasable
    ? "Bientôt disponible"
    : hasVariants
    ? "Choisir"
    : "Ajouter au panier";

  return (
    <div
      data-testid={`shop-product-${product.id}`}
      className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-transparent hover:border-gold transition-all duration-300 hover:-translate-y-1"
      style={{ boxShadow: "0 8px 30px rgba(42,22,8,0.06)" }}
    >
      <Link to={`/boutique/${product.id}`} data-testid={`shop-image-link-${product.id}`} className="relative aspect-[4/3] overflow-hidden bg-[#0a0e1a] block">
        {img && <img src={img} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
        {product.badge && (
          <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider shadow-md ${BADGE_STYLES[product.badge] || "bg-brown-dark text-ivory"}`} data-testid={`shop-badge-${product.id}`}>
            {product.badge}
          </span>
        )}
        {product.format && (
          <span className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-ivory/90 backdrop-blur-sm text-[11px] font-semibold text-brown-dark border border-gold/30">
            {product.format}
          </span>
        )}
      </Link>
      <div className="flex flex-col flex-1 p-5">
        <span className="text-[11px] uppercase tracking-wide text-gold font-semibold">{product.category}</span>
        <h3 className="font-heading text-xl font-medium text-brown-black leading-snug mt-1">
          <Link to={`/boutique/${product.id}`} data-testid={`shop-title-link-${product.id}`} className="hover:text-gold transition-colors">{product.name}</Link>
        </h3>
        <p className="text-sm text-brown-black/60 mt-1.5 leading-relaxed flex-1 line-clamp-2">{product.description}</p>
        <div className="flex items-center gap-2 mt-4">
          {hasVariants && <span className="text-xs text-brown-black/50">dès</span>}
          <span className="font-heading text-2xl font-semibold text-gold" data-testid={`shop-price-${product.id}`}>{displayPrice.toFixed(2)} €</span>
          {product.old_price > 0 && product.old_price > displayPrice && (
            <span className="text-sm text-brown-black/40 line-through">{Number(product.old_price).toFixed(2)} €</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-4">
          {isExternal ? (
            <a href={purchase.url} target="_blank" rel="noopener noreferrer" data-testid={`shop-add-${product.id}`} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 bg-gold text-white hover:bg-gold-light">
              <ExternalLink size={15} className="shrink-0" /> {buttonLabel}
            </a>
          ) : <button
            onClick={handleAdd}
            disabled={!purchasable}
            data-testid={`shop-add-${product.id}`}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${purchasable ? "bg-gold text-white hover:bg-gold-light" : "bg-brown-dark/10 text-brown-dark/50 cursor-not-allowed"}`}
          >
            {isExternal ? <ExternalLink size={15} /> : !purchasable ? <Clock size={15} /> : <ShoppingCart size={15} />} {buttonLabel}
          </button>}
          <Link
            to={`/boutique/${product.id}`}
            data-testid={`shop-view-${product.id}`}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-gold/40 text-gold hover:bg-gold/10 transition-colors duration-300"
            aria-label="Voir le produit"
          >
            <Eye size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ShopProductCard;
